import React, { useEffect, useState } from 'react';
import api from '../api';
import { Save, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';

const PRESETS = [
  { name: 'Sendgrid', smtp_host: 'smtp.sendgrid.net', smtp_port: 587, smtp_user: 'apikey', use_tls: 1, hint: 'Use "apikey" como usuário e sua API key como senha.' },
  { name: 'Mailgun',  smtp_host: 'smtp.mailgun.org',  smtp_port: 587, smtp_user: '', use_tls: 1, hint: 'Crie SMTP credentials no painel Mailgun.' },
  { name: 'AWS SES',  smtp_host: 'email-smtp.us-east-1.amazonaws.com', smtp_port: 587, smtp_user: '', use_tls: 1, hint: 'Gere credenciais SMTP no console AWS SES.' },
  { name: 'Brevo (Sendinblue)', smtp_host: 'smtp-relay.brevo.com', smtp_port: 587, smtp_user: '', use_tls: 1, hint: 'Pegue chave SMTP em Brevo → SMTP & API.' },
  { name: 'Mailjet',  smtp_host: 'in-v3.mailjet.com',  smtp_port: 587, smtp_user: '', use_tls: 1, hint: 'API Key Public = user, Secret = senha.' },
  { name: 'SMTP2GO',  smtp_host: 'mail.smtp2go.com',   smtp_port: 587, smtp_user: '', use_tls: 1, hint: 'Crie usuário SMTP em SMTP2GO.' },
  { name: 'Custom',   smtp_host: '', smtp_port: 587, smtp_user: '', use_tls: 1, hint: 'Configure manualmente.' },
];

export default function MailRelay() {
  const [form, setForm] = useState({
    enabled: false, provider: 'Sendgrid',
    smtp_host: 'smtp.sendgrid.net', smtp_port: 587,
    smtp_user: 'apikey', smtp_password: '', use_tls: true,
  });
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState('');
  const [output, setOutput] = useState('');
  const [testTo, setTestTo] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/email/relay');
      if (r.data) {
        setForm({
          enabled: !!r.data.enabled,
          provider: r.data.provider || 'Custom',
          smtp_host: r.data.smtp_host,
          smtp_port: r.data.smtp_port,
          smtp_user: r.data.smtp_user || '',
          smtp_password: '',
          use_tls: !!r.data.use_tls,
        });
      }
    } catch {}
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  const applyPreset = (name) => {
    const p = PRESETS.find(x => x.name === name) || PRESETS[0];
    setForm({ ...form, provider: p.name, smtp_host: p.smtp_host, smtp_port: p.smtp_port, smtp_user: p.smtp_user, use_tls: !!p.use_tls });
  };

  const save = async () => {
    setMsg('Salvando…'); setOutput('');
    try {
      const r = await api.post('/email/relay', form);
      setMsg('OK'); setOutput(r.data.output || '');
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); setOutput(e.response?.data?.output || ''); }
  };

  const test = async () => {
    if (!testTo) return;
    setMsg('Enviando teste…'); setOutput('');
    try {
      const r = await api.post('/email/relay/test', { to: testTo });
      setMsg('Teste enviado — verifique caixa de entrada e logs do Postfix');
      setOutput(r.data.output || '');
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); setOutput(e.response?.data?.output || ''); }
  };

  const preset = PRESETS.find(p => p.name === form.provider) || PRESETS[PRESETS.length - 1];
  if (!loaded) return <p style={{color:'#a1a1aa'}}>Carregando…</p>;

  return (
    <>
      <div className="page-title">
        <h1>SMTP Relay (Smart Host)</h1>
      </div>

      <div style={{padding:'1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', marginBottom:'1.5rem', fontSize:'.88rem'}}>
        <strong style={{color:'#f59e0b'}}><AlertTriangle size={14} style={{verticalAlign:'middle'}}/> Por que usar relay?</strong>
        <p style={{margin:'.5rem 0 0', color:'#d4d4d8'}}>
          IPs novos ou de ISPs residenciais geralmente são bloqueados pelo Spamhaus, Outlook, Gmail.
          Habilitando relay, seu Postfix envia através de um serviço com IPs limpos — emails são entregues normalmente
          enquanto você trabalha pra "esquentar" seu próprio IP (PTR + delisting Spamhaus + reputação).
        </p>
        <p style={{margin:'.5rem 0 0', color:'#a1a1aa', fontSize:'.82rem'}}>
          O relay NÃO afeta o RECEBIMENTO — você continua recebendo emails diretamente no seu MX.
        </p>
      </div>

      <label>Provedor SMTP</label>
      <select value={form.provider} onChange={e => applyPreset(e.target.value)}>
        {PRESETS.map(p => <option key={p.name}>{p.name}</option>)}
      </select>
      {preset.hint && <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>{preset.hint}</p>}

      <div className="row" style={{marginTop:'.6rem'}}>
        <div style={{flex:2}}>
          <label>SMTP host</label>
          <input value={form.smtp_host} onChange={e => setForm({...form, smtp_host: e.target.value})} placeholder="smtp.exemplo.com" />
        </div>
        <div style={{maxWidth:'130px'}}>
          <label>Porta</label>
          <input type="number" value={form.smtp_port} onChange={e => setForm({...form, smtp_port: +e.target.value})} />
        </div>
      </div>

      <div className="row">
        <div>
          <label>Usuário SMTP</label>
          <input value={form.smtp_user} onChange={e => setForm({...form, smtp_user: e.target.value})} />
        </div>
        <div>
          <label>Senha / API key</label>
          <input type="password" value={form.smtp_password} onChange={e => setForm({...form, smtp_password: e.target.value})}
            placeholder={form.enabled ? '(vazio = manter atual)' : 'sua chave secreta'} />
        </div>
      </div>

      <label style={{display:'flex', alignItems:'center', gap:'.5rem', marginTop:'.6rem'}}>
        <input type="checkbox" style={{width:'auto'}} checked={form.use_tls} onChange={e => setForm({...form, use_tls: e.target.checked})} />
        Forçar TLS (recomendado)
      </label>

      <label style={{display:'flex', alignItems:'center', gap:'.5rem', marginTop:'.5rem', padding:'.6rem', border:'1px solid ' + (form.enabled ? '#10b981' : '#27272a'), borderRadius:'6px'}}>
        <input type="checkbox" style={{width:'auto'}} checked={form.enabled} onChange={e => setForm({...form, enabled: e.target.checked})} />
        <CheckCircle2 size={14} color={form.enabled ? '#10b981' : '#52525b'}/>
        <strong>Habilitar relay</strong>
        <span style={{color:'#a1a1aa', fontSize:'.78rem'}}>
          {form.enabled ? '✓ Postfix vai relayar emails de saída via este host' : 'Desabilitado — Postfix envia direto'}
        </span>
      </label>

      <div style={{marginTop:'1rem', display:'flex', gap:'.6rem'}}>
        <button onClick={save}><Save size={12}/> Salvar e aplicar</button>
      </div>

      {form.enabled && (
        <>
          <h3 style={{marginTop:'1.5rem'}}>Testar envio</h3>
          <div className="row">
            <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="seu-email-de-teste@gmail.com" />
            <div style={{flex:'none'}}>
              <button onClick={test} disabled={!testTo} className="secondary"><Send size={12}/> Enviar teste</button>
            </div>
          </div>
        </>
      )}

      {msg && <div style={{marginTop:'1rem', color: msg.includes('Erro') || msg.includes('erro') ? '#ef4444' : '#10b981'}}>{msg}</div>}
      {output && <pre className="code" style={{marginTop:'.5rem', maxHeight:'300px', overflow:'auto', fontSize:'.78rem'}}>{output}</pre>}

      <div style={{marginTop:'2rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
        <strong style={{color:'#3b82f6'}}>Para reabilitar envio DIRETO no futuro:</strong>
        <ol style={{margin:'.5rem 0 0 1.2rem', padding:0, color:'#a1a1aa'}}>
          <li>Configure PTR/rDNS junto ao provedor do servidor: cada IP deve resolver via dig -x para um hostname FQDN</li>
          <li>Solicite delisting na Spamhaus: <a href="https://www.spamhaus.org/query/ip/203.0.113.10" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>spamhaus.org/query/ip/203.0.113.10</a></li>
          <li>Garanta SPF + DKIM + DMARC corretos (já configurados no painel)</li>
          <li>"Warm up" do IP: envie volume baixo gradualmente</li>
          <li>Desabilite o relay aqui após reputação melhorar</li>
        </ol>
      </div>
    </>
  );
}
